import { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Plus, Apple, Heart, Flame, UtensilsCrossed, Ban, Coffee, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const BACKEND_URL = '';

const SectionCard = ({ title, icon, children, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4, delay }}
  >
    <Card className="bg-card border-border" data-testid={`section-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-3 text-base md:text-lg font-bold uppercase tracking-wide">
          {icon}
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 text-sm md:text-base text-muted-foreground leading-relaxed">
        {children}
      </CardContent>
    </Card>
  </motion.div>
);

const RuleBlock = ({ number, title, children }) => (
  <div className="border-l-2 border-primary/40 pl-4 py-1">
    <h4 className="text-white font-bold text-sm md:text-base mb-2">{number}. {title}</h4>
    {children}
  </div>
);

const BulletList = ({ items }) => (
  <ul className="space-y-1.5 ml-1">
    {items.map((item, i) => (
      <li key={i} className="flex items-start gap-2">
        <span className="text-primary mt-1.5 text-[8px]">&#9679;</span>
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

const MealCard = ({ emoji, title, ingredients, tip }) => (
  <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-4">
    <h5 className="text-white font-bold text-sm mb-2">{emoji} {title}</h5>
    <BulletList items={ingredients} />
    {tip && <p className="text-primary/80 text-xs mt-2 italic">{tip}</p>}
  </div>
);

const CollapsibleMealSection = ({ title, icon, meals, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-zinc-800 rounded-sm overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 bg-zinc-900/70 hover:bg-zinc-900 transition-colors"
        data-testid={`meal-section-${title.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <span className="flex items-center gap-2 font-bold text-sm md:text-base text-white uppercase tracking-wide">
          {icon} {title}
        </span>
        {open ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
      </button>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3"
        >
          {meals.map((meal, i) => (
            <MealCard key={i} {...meal} />
          ))}
        </motion.div>
      )}
    </div>
  );
};

const Nutrition = () => {
  const [tips, setTips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({ title: '', content: '', category: 'Protein' });

  const fetchTips = async () => {
    try {
      const response = await axios.get(`/api/nutrition`, { withCredentials: true });
      setTips(response.data);
    } catch (error) {
      toast.error('Failed to fetch nutrition tips');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTips(); }, []);

  const handleCreateTip = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`/api/nutrition`, formData, { withCredentials: true });
      toast.success('Nutrition tip added successfully!');
      setDialogOpen(false);
      setFormData({ title: '', content: '', category: 'Protein' });
      fetchTips();
    } catch (error) {
      toast.error('Failed to add nutrition tip');
    }
  };

  const categoryColors = {
    'Protein': 'bg-red-500/10 text-red-400 border-red-500/20',
    'Carbs': 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
    'Fats': 'bg-green-500/10 text-green-400 border-green-500/20',
    'Hydration': 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    'Supplements': 'bg-purple-500/10 text-purple-400 border-purple-500/20',
    'Meal Timing': 'bg-orange-500/10 text-orange-400 border-orange-500/20'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4 md:p-8">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-3xl md:text-5xl font-bold tracking-tight uppercase mb-2">Nutrition</h1>
            <p className="text-base md:text-lg text-muted-foreground">Fuel your transformation with balanced meals</p>
          </div>
          {/* Tips managed by admin */}
        </div>

        {/* Intro */}
        <SectionCard title="The Iron Dad Way" icon={<Heart className="w-5 h-5 text-primary" />} delay={0.05}>
          <p>Don't even think about denying yourself the simple joys of the foods and drinks you love. Life's too precious to completely cut out that glass of whiskey, that cold beer, or that rich, velvety red wine—enjoy them, savor them, and let them be parts of your journey. Indulge in a chocolate treat, a scoop of ice cream—these small moments of pleasure are what make life worth fighting for.</p>
          <p>Make a change not by deprivation, but by choosing to eat smaller meals, using a small plate or bowl—transform your habits without losing the joy. Resist the urge to go back for seconds; that decision is what helped me shed kilos and crush my cravings once and for all because who has time or can be bothered weighing all of your meals…not me.</p>
          <p>And remember, don't sneak an extra bite of your kid's food or snatch a Lollipop now and then. Stay strong, stay focused. Keep your eyes on the prize, because you're worth every effort. Stay the course—you've got this!</p>
        </SectionCard>

        {/* The Rules */}
        <SectionCard title='The "Iron Dad Fat Loss System"' icon={<Flame className="w-5 h-5 text-red-500" />} delay={0.1}>
          <div className="space-y-5">
            <RuleBlock number={1} title="Same Breakfast Every Day">
              <p className="mb-2">High protein. No decisions.</p>
              <p className="text-white text-xs font-semibold uppercase tracking-wider mb-1">Options:</p>
              <BulletList items={['Eggs + toast', 'Protein smoothie', 'Muesli + Greek yogurt + fruit']} />
            </RuleBlock>

            <RuleBlock number={2} title="Eat 2-3 Real Meals">
              <BulletList items={['Meal 1 (morning)', 'Meal 2 (afternoon)', 'Meal 3 (dinner)']} />
              <p className="text-primary/80 text-xs mt-2 italic">If you're hungry between — you didn't eat enough protein.</p>
            </RuleBlock>

            <RuleBlock number={3} title="Protein with Every Meal">
              <p className="mb-2">Simple visual rule:</p>
              <BulletList items={['1-2 palms of protein first', 'Then everything else']} />
              <p className="text-white text-xs font-semibold uppercase tracking-wider mt-2 mb-1">Examples:</p>
              <p>Chicken, steak, eggs, tuna, protein shake</p>
            </RuleBlock>

            <RuleBlock number={4} title="Liquids">
              <BulletList items={[
                'Coffee (no brainer) — I have two double shot small lattes per day',
                'Water — 2-3 litres',
                'Alcohol — Limit to 1-2 per week'
              ]} />
              <div className="mt-2 bg-zinc-900/50 border border-zinc-800 rounded-sm p-3 text-xs space-y-1">
                <p><span className="text-white font-semibold">1.</span> Alcohol is empty calories</p>
                <p><span className="text-white font-semibold">2.</span> Metabolic disruption — the body prioritises metabolising it over other nutrients, meaning fat burning is temporarily halted</p>
                <p><span className="text-white font-semibold">3.</span> Affects hormones and increases appetite</p>
                <p><span className="text-white font-semibold">4.</span> Alcohol is a depressant and can affect our moods</p>
              </div>
            </RuleBlock>

            <RuleBlock number={5} title="One Plate Rule">
              <BulletList items={['One plate per meal', 'Use a smaller plate or bowl', 'No seconds']} />
              <p className="text-primary/80 text-xs mt-2 italic">Wait 10 mins if still hungry.</p>
            </RuleBlock>

            <RuleBlock number={6} title="Snacks">
              <BulletList items={['Fruit', 'Protein shake', 'Muesli bar', 'Anything you like as long as it\'s just one!']} />
            </RuleBlock>
          </div>
        </SectionCard>

        {/* Simple Meal Template */}
        <SectionCard title="Simple Meal Template" icon={<UtensilsCrossed className="w-5 h-5 text-blue-400" />} delay={0.15}>
          <p className="text-white font-semibold text-xs uppercase tracking-wider">No tracking needed. Each meal:</p>
          <BulletList items={['Protein (main focus)', 'Veg (bulk it up)', 'Carbs (controlled, not removed)']} />
          <p className="text-white font-semibold text-xs uppercase tracking-wider mt-3">Example:</p>
          <BulletList items={['Steak + potatoes + salad', 'Chicken + rice + greens', 'Eggs + toast + spinach or avocado']} />
        </SectionCard>

        {/* What to Avoid */}
        <SectionCard title="What to Avoid" icon={<Ban className="w-5 h-5 text-red-400" />} delay={0.2}>
          <BulletList items={[
            'Constant snacking (overindulging)',
            '"All or nothing" mindset',
            'Skipping meals — then binging',
            'Sauces — Every bit of oil and sauce adds up quickly. Avoid sauces where possible.'
          ]} />
        </SectionCard>

        {/* Meal Ideas - Collapsible */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.25 }} className="space-y-3">
          <h2 className="text-lg md:text-xl font-bold uppercase tracking-wide flex items-center gap-2">
            <Coffee className="w-5 h-5 text-primary" /> Meal Ideas
          </h2>

          <CollapsibleMealSection
            title="Breakfast Options"
            icon="🍳"
            defaultOpen={true}
            meals={[
              { emoji: '1.', title: 'Loaded Eggs on Toast', ingredients: ['Eggs (scrambled or fried)', 'Sourdough toast', 'Spinach + feta + avo'], tip: 'Tastes like a cafe meal, takes 5-10 mins' },
              { emoji: '2.', title: 'Protein Smoothie', ingredients: ['Protein powder', 'Banana', 'Peanut butter', 'Milk (or almond milk)', 'Ice'], tip: 'Thick, filling, and easy to drink on the go' },
              { emoji: '3.', title: 'Greek Yogurt Bowl', ingredients: ['Greek yogurt', 'Frozen berries', 'Honey', 'Granola'], tip: 'Sweet, high protein, feels like dessert' },
              { emoji: '4.', title: 'Breakfast Wrap', ingredients: ['Eggs', 'Bacon or ham', 'Cheese', 'Wrap'], tip: 'Portable + satisfying' },
              { emoji: '5.', title: 'On the Go Dad Option', ingredients: ['Protein shake + banana'], tip: 'Your "no excuses" fallback' },
            ]}
          />

          <CollapsibleMealSection
            title="Lunch Options"
            icon="🥪"
            meals={[
              { emoji: '1.', title: 'Chicken Wrap', ingredients: ['Rotisserie chicken', 'Wrap', 'Lettuce, tomato, mayo or aioli'], tip: 'Takes 3 mins, tastes unreal' },
              { emoji: '2.', title: 'Steak Sandwich', ingredients: ['Steak', 'Toast or roll', 'Cheese + onion + sauce'], tip: 'High protein but feels like a treat' },
              { emoji: '3.', title: 'Tuna Bowl', ingredients: ['Tuna', 'Rice (microwave packet)', 'Avocado', 'Soy sauce or mayo'], tip: 'Cheap, fast, filling' },
              { emoji: '4.', title: 'Eggs on Toast', ingredients: ['3-4 eggs', 'Toast', 'Salt, pepper, chili flakes'], tip: 'Simple but very effective for fat loss' },
            ]}
          />

          <CollapsibleMealSection
            title="Dinner Options"
            icon="🍽️"
            meals={[
              { emoji: '1.', title: 'Steak + Potatoes + Greens', ingredients: ['Steak', 'Roast or air-fried potatoes', 'Broccoli or salad'], tip: 'Classic, filling, satisfying' },
              { emoji: '2.', title: 'Chicken Bowl', ingredients: ['Chicken (grilled or air-fried)', 'Rice', 'Veg', 'Sauce (BBQ, peri peri, teriyaki)'], tip: 'Easy and repeatable' },
              { emoji: '3.', title: 'Spaghetti Bolognese', ingredients: ['Lean mince', 'Pasta', 'Jar sauce', 'Add extra veg (zucchini, carrot)'], tip: 'Comfort food that still works' },
              { emoji: '4.', title: 'Tacos / Burrito Bowl', ingredients: ['Mince or chicken', 'Rice or wraps', 'Cheese, salsa, lettuce, avo'], tip: 'Flexible and hard to get sick of' },
              { emoji: '5.', title: 'Salmon + Rice + Veg', ingredients: ['Pan-fried/Oven baked salmon', 'Rice', 'Greens'], tip: 'Simple, healthy, actually tastes good' },
            ]}
          />
        </motion.div>

        {/* Community Tips */}
        <div className="pt-4">
          <h2 className="text-lg md:text-xl font-bold uppercase tracking-wide mb-4">Community Tips</h2>
          {tips.length === 0 ? (
            <div className="text-center py-8">
              <Apple className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
              <p className="text-muted-foreground text-sm">No community tips yet. Add your first tip to get started!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {tips.map((tip, index) => (
                <motion.div key={tip.tip_id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: index * 0.05 }}>
                  <Card data-testid={`nutrition-tip-card-${tip.tip_id}`} className="bg-card border-border hover:border-primary/50 transition-colors duration-300 h-full">
                    <CardHeader>
                      <div className="mb-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider border ${categoryColors[tip.category] || 'bg-zinc-800 text-zinc-300 border-zinc-700'}`}>{tip.category}</span>
                      </div>
                      <CardTitle className="text-base">{tip.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground text-sm leading-relaxed">{tip.content}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Nutrition;
